import React from 'react';

import { Integration } from 'src/common/interfaces/integration.interface';

const CHROME_EXTENSION_ID = 'ndabgakchppcfcececfkebenibnfnkei';

const proGatedIntegrations = ['slack', 'segment', 'zapier'];

const enterpriseGatedIntegrations = ['hubspot', 'gong'];

const integrations: Integration[] = [
  {
    code: 'slack',
    name: 'Slack',
    description: 'Post in a Slack channel whenever users interact with your Journeys',
    how_does_it_work_copy: `
      Post in a Slack channel whenever users interact with your Journeys.
      You're able to specify Slack channels that we should post in for whenever a Journey is opened or interacted with (comments, reactions and emails captured.)
    `,
    paywall_work_copy: `
    Post in a Slack channel whenever users interact with your Journeys.
    You're able to specify Slack channels that we should post in for whenever a Journey is opened or interacted with (comments, reactions and emails captured.). Upgrade to Pro plan to use Slack integration.
  `,
    is_paragon: true,
  },
  {
    code: 'box',
    name: 'Box',
    description: "Connect to your users' Box account to manage their access to Journeys",
    how_does_it_work_copy: `
     Connect your box account and add content directly from box
    `,
    is_paragon: true,
  },
  {
    code: 'segment',
    name: 'Segment',
    description: 'Send events when users interact with your Journeys.',
    how_does_it_work_copy: (
      <div className='flex flex-col space-y-4 text-bedrock-p'>
        <span className=''>
          Send events when users interact with your Journeys. It is Segment best practice to create a new Segment Source
          for Journey, but you can also use an existing write key. We'll then write Segment events whenever a Journey is
          opened or interacted with (comments, reactions and emails captured). We'll also send identify calls if we know
          the users email address.
        </span>
        <a
          href='https://segment.com/docs/connections/find-writekey/'
          className='underline'
          target='_blank'
          rel='noopener noreferrer'
        >
          How do I find my write key?
        </a>
      </div>
    ),
    paywall_work_copy: (
      <div className='flex flex-col space-y-4 text-bedrock-p'>
        <span className=''>
          Send events when users interact with your Journeys. We'll write Segment events whenever a Journey is opened or
          interacted with (comments, reactions and emails captured). We'll also send identify calls if we know the users
          email address.
        </span>
        <br />
        <span>Upgrade to the Pro plan to use Segment in Journeys</span>
      </div>
    ),
    form_placeholder: 'Segment Write Key',
    is_api_key: true,
    allowMultiple: true,
  },
  {
    code: 'google-drive',
    name: 'Google Drive',
    description: 'Import content from Google Drive and use in your Journeys',
    how_does_it_work_copy: `
      Integrate Google Drive with Journey to enable search and one click importing of content into Journeys.
      When you select a file from within Google Drive, it will be made publicly accessible by Google Drive in order for recipients to access the file.
    `,
    paywall_work_copy: `
    Integrate Google Drive with Journey to enable search and one click importing of content into Journeys.
    When you select a file from within Google Drive, it will be made publicly accessible by Google Drive in order for recipients to access the file. Upgrade to Pro to use Google Drive Integration
  `,
    onboarding_copy: (
      <div>
        <div>Import content from Google Drive and use in your Journeys. </div>
        <div>
          Once integrated, you will be able to pull in documents from Google Slides, Google Docs, Google Forms and
          Google Sheets.
        </div>
      </div>
    ),
    is_oauth: true,
    allowMultiple: true,
  },
  {
    code: 'zapier',
    name: 'Zapier',
    description: 'Send actions when users interact with Journeys',
    how_does_it_work_copy: `
    We send Journey events as “actions” to Zapier. 
    For example you could create a zap whose action is “Email Captured” (from Journey) and then write that captured email to a Google Sheet or Airtable.
    `,
    paywall_work_copy: `
    We send Journey events as “actions” to Zapier. 
    For example you could create a zap whose action is “Email Captured” (from Journey) and then write that captured email to a Google Sheet or Airtable. Upgrade to Pro to use Zapier integration.
    `,
    form_placeholder: 'Install Journey App (Beta)',
    is_static: true,
  },
  {
    code: 'calendly',
    name: 'Calendly',
    description: 'Use your Calendly inside your Journeys to help book meetings',
    is_embed: true,
    is_oauth: false,
    is_api_key: false,
  },
  {
    code: 'typeform',
    name: 'Typeform',
    description: 'Add a Typeform to encourage your recipients to provide information to you',
    is_embed: true,
    is_oauth: false,
    is_api_key: false,
  },
  {
    code: 'loom',
    name: 'Loom',
    description: 'Add an existing Loom video to play in your Journeys',
    is_embed: true,
    is_oauth: false,
    is_api_key: false,
  },
  {
    code: 'gong',
    name: 'Gong',
    description: 'Add recordings from Gong and use them in your Journeys',
    how_does_it_work_copy: `
      Use your Gong recordings inside your Journeys to help close deals.
      Once connected, you will be able to add your Gong recordings without leaving Journey.
    `,
    paywall_work_copy: `
    Use your Gong recordings inside your Journeys to help close deals.
    Once connected, you will be able to add your Gong recordings without leaving Journey. Upgrade to Enterprise to use Gong integration
  `,
    paywall_type: 'enterprise',
    is_oauth: true,
  },
  {
    code: 'hubspot',
    name: 'Hubspot Sync',
    description: 'Connect your Hubspot account to sync records from your Hubspot.',
    is_oauth: true,
    is_static: false,
    is_paragon: false,
    how_does_it_work_copy: (
      <div className='flex flex-col space-y-2 text-bedrock-p'>
        <p>
          Connect your Hubspot account to sync records from your Hubspot CRM. Enable your sales team to close more deals
          by keeping your Hubspot CRM records up to date - without manual data entry.
        </p>
        <p>Our Hubspot integration enables you to:</p>
        <div className='flex flex-col space-y-1'>
          <div>- Automatically create or update records like contacts or deals in Hubspot</div>
          <div>- Sync records from Hubspot</div>
          <div>- Receive updates when new records are created in Hubspot</div>
        </div>
      </div>
    ),
    paywall_work_copy: (
      <div className='flex flex-col space-y-2 text-bedrock-p'>
        <p>
          Connect your Hubspot account to sync records from your Hubspot CRM. Enable your sales team to close more deals
          by keeping your Hubspot CRM records up to date - without manual data entry.
        </p>
        <p>Our Hubspot integration enables you to:</p>
        <div className='flex flex-col space-y-1'>
          <div>- Automatically create or update records like contacts or deals in Hubspot</div>
          <div>- Sync records from Hubspot</div>
          <div>- Receive updates when new records are created in Hubspot</div>
        </div>
        <p>Upgrade to enterprise plan to use Hubspot Sync integration</p>
      </div>
    ),
    paywall_type: 'enterprise',
    apiKeyConnectedLabel: 'Saved',
  },
  {
    code: 'hubspot-tracker',
    parentCode: 'hubspot',
    name: 'Hubspot Tracker',
    description: 'Add the Hubspot Tracking Code to trigger with every Journey open',
    is_api_key: true,
    how_does_it_work_copy: (
      <div className='flex flex-col space-y-4 text-bedrock-p'>
        <span className=''>We’ll fire your Hubspot Tracking Code whenever your Journeys are opened.</span>
        <a
          href='https://knowledge.hubspot.com/reports/install-the-hubspot-tracking-code'
          className='underline'
          target='_blank'
          rel='noopener noreferrer'
        >
          How do I find this?
        </a>
      </div>
    ),
    paywall_work_copy: (
      <div className='flex flex-col space-y-4 text-bedrock-p'>
        <span className=''>We’ll fire your Hubspot Tracking Code whenever your Journeys are opened.</span>
        <span>Upgrade to Enterprise plan to use Hubspot Tracker integration</span>
      </div>
    ),
    paywall_type: 'enterprise',
    sidebar: {
      description: (
        <div className='flex flex-col items-start space-y-4 text-bedrock-p'>
          <span className=''>
            Please enter the number that appears in your tracking code. Here’s an example of where you’ll find it below.
          </span>
          <div className='text-bedrock-code-regular p-4 bg-bedrock-light-gray rounded-lg'>
            {`<!-- Start of HubSpot Embed Code -->`}
            <br />
            {`<script type="text/javascript" id="hs-script-loader" async defer src="//js.hs-scripts.com/`}
            <span className='text-bedrock-code-strong'>2009314</span>
            {`.js"></script>`}
            <br />
            {`<!-- End of HubSpot Embed Code -->`}
          </div>
          <a
            href='https://knowledge.hubspot.com/reports/install-the-hubspot-tracking-code'
            className='underline'
            target='_blank'
            rel='noopener noreferrer'
          >
            Read more here
          </a>
        </div>
      ),
      placeholder: '2009314',
    },
    apiKeyConnectedLabel: 'Saved',
  },
  {
    code: 'chrome-extension',
    name: 'Chrome Extension',
    description: 'Quickly create and insert Journey links from within Google Chrome',
    enableCopy: 'Installed',
    disableCopy: 'Install',
    target: `https://chrome.google.com/webstore/detail/journey/${CHROME_EXTENSION_ID}`,
  },
];

const listedIntegrations = integrations.filter((integration) => !integration.is_embed);

export { integrations, listedIntegrations, proGatedIntegrations, enterpriseGatedIntegrations };
